import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
} from 'react-native';
import {
  Text,
  Card,
  useTheme,
  Chip,
  IconButton,
  Surface,
  Button,
  ProgressBar,
} from 'react-native-paper';
import type { MasteryState } from '@coffeeplexity/shared';
import { ConfidenceIndicator } from './ConfidenceIndicator';

const { width: screenWidth } = Dimensions.get('window');

export interface SkillGap {
  skillId: string;
  skillName: string;
  category: string;
  currentLevel: number;
  targetLevel: number;
  gap: number;
  priority: 'low' | 'medium' | 'high' | 'critical';
  blockedSkills: string[];
  prerequisites: string[];
  estimatedTimeToClose: number; // in minutes
  difficulty: number; // 0-1
  aiRecommendations: GapRecommendation[];
  learningPath: LearningStep[];
}

export interface GapRecommendation {
  id: string;
  type: 'content' | 'practice' | 'mentor' | 'course';
  title: string;
  description: string;
  contentId?: string;
  estimatedImpact: number; // 0-1
  timeRequired: number; // in minutes
  difficulty: number; // 0-1
  confidence: number; // AI confidence in recommendation
  reasoning: string;
}

export interface LearningStep {
  id: string;
  title: string;
  description: string;
  type: 'theory' | 'practice' | 'assessment' | 'project';
  estimatedTime: number;
  prerequisites: string[];
  skills: string[];
  resources: LearningResource[];
}

export interface LearningResource {
  id: string;
  type: 'video' | 'article' | 'recipe' | 'exercise' | 'quiz';
  title: string;
  url?: string;
  duration?: number;
  difficulty: number;
}

export interface SkillDependency {
  skillId: string;
  dependsOn: string[];
  blocks: string[];
  weight: number; // importance of this dependency
}

interface SkillGapAnalyzerProps {
  masteryStates: MasteryState[];
  skillGaps: SkillGap[];
  skillDependencies: SkillDependency[];
  targetSkillLevels: Record<string, number>;
  onGapFocus: (skillId: string) => void;
  onRecommendationSelect: (recommendation: GapRecommendation) => void;
  onLearningPathStart: (skillId: string, path: LearningStep[]) => void;
  style?: any;
}

type ViewMode = 'priority' | 'category' | 'timeline' | 'dependencies';
type FilterMode = 'all' | 'critical' | 'high' | 'medium' | 'low';

export function SkillGapAnalyzer({
  masteryStates,
  skillGaps,
  skillDependencies,
  targetSkillLevels,
  onGapFocus,
  onRecommendationSelect,
  onLearningPathStart,
  style,
}: SkillGapAnalyzerProps) {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>('priority');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expandedGaps, setExpandedGaps] = useState<Set<string>>(new Set());

  // Group gaps by category
  const gapsByCategory = useMemo(() => {
    const grouped: Record<string, SkillGap[]> = {};
    skillGaps.forEach(gap => {
      if (!grouped[gap.category]) {
        grouped[gap.category] = [];
      }
      grouped[gap.category].push(gap);
    });
    return grouped;
  }, [skillGaps]);

  // Filter and sort gaps
  const filteredGaps = useMemo(() => {
    let filtered = skillGaps;

    if (filterMode !== 'all') {
      filtered = filtered.filter(gap => gap.priority === filterMode);
    }

    if (selectedCategory) {
      filtered = filtered.filter(gap => gap.category === selectedCategory);
    }

    // Sort based on view mode
    switch (viewMode) {
      case 'priority':
        const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
        return filtered.sort((a, b) => {
          const aPriority = priorityOrder[a.priority] + (1 - a.gap);
          const bPriority = priorityOrder[b.priority] + (1 - b.gap);
          return bPriority - aPriority;
        });

      case 'timeline':
        return filtered.sort(
          (a, b) => a.estimatedTimeToClose - b.estimatedTimeToClose
        );

      case 'category':
        return filtered.sort((a, b) => {
          if (a.category !== b.category) {
            return a.category.localeCompare(b.category);
          }
          return b.gap - a.gap;
        });

      default:
        return filtered;
    }
  }, [skillGaps, filterMode, selectedCategory, viewMode]);

  // Calculate overall statistics
  const gapStats = useMemo(() => {
    const totalGaps = skillGaps.length;
    const criticalGaps = skillGaps.filter(
      g => g.priority === 'critical'
    ).length;
    const highGaps = skillGaps.filter(g => g.priority === 'high').length;
    const averageGap =
      skillGaps.reduce((sum, g) => sum + g.gap, 0) / Math.max(totalGaps, 1);
    const totalTimeToClose = skillGaps.reduce(
      (sum, g) => sum + g.estimatedTimeToClose,
      0
    );

    return {
      totalGaps,
      criticalGaps,
      highGaps,
      averageGap,
      totalTimeToClose,
    };
  }, [skillGaps]);

  const handleGapToggle = useCallback((gapId: string) => {
    setExpandedGaps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(gapId)) {
        newSet.delete(gapId);
      } else {
        newSet.add(gapId);
      }
      return newSet;
    });
  }, []);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return '#D32F2F';
      case 'high':
        return '#F57C00';
      case 'medium':
        return '#1976D2';
      case 'low':
        return '#388E3C';
      default:
        return theme.colors.outline;
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'alert-circle';
      case 'high':
        return 'alert';
      case 'medium':
        return 'information';
      case 'low':
        return 'check-circle';
      default:
        return 'help-circle';
    }
  };

  const formatTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}min`
      : `${hours}h`;
  };

  const renderGapCard = (gap: SkillGap) => {
    const isExpanded = expandedGaps.has(gap.skillId);
    const priorityColor = getPriorityColor(gap.priority);

    return (
      <Card key={gap.skillId} style={styles.gapCard}>
        <TouchableOpacity
          onPress={() => handleGapToggle(gap.skillId)}
          style={styles.gapHeader}
        >
          <View style={styles.gapHeaderContent}>
            <View style={styles.gapInfo}>
              <View style={styles.gapTitleRow}>
                <IconButton
                  icon={getPriorityIcon(gap.priority)}
                  size={20}
                  iconColor={priorityColor}
                />
                <View style={styles.gapTitleContent}>
                  <Text variant='titleMedium' style={styles.gapTitle}>
                    {gap.skillName}
                  </Text>
                  <Text variant='bodySmall' style={styles.gapCategory}>
                    {gap.category}
                  </Text>
                </View>
              </View>

              {/* Progress Visualization */}
              <View style={styles.progressSection}>
                <View style={styles.progressLabels}>
                  <Text variant='bodySmall' style={styles.progressLabel}>
                    Current: {Math.round(gap.currentLevel * 100)}%
                  </Text>
                  <Text variant='bodySmall' style={styles.progressLabel}>
                    Target: {Math.round(gap.targetLevel * 100)}%
                  </Text>
                </View>
                <View style={styles.progressContainer}>
                  <View style={styles.progressTrack}>
                    <View
                      style={[
                        styles.currentProgress,
                        {
                          width: `${gap.currentLevel * 100}%`,
                          backgroundColor: theme.colors.primary,
                        },
                      ]}
                    />
                    <View
                      style={[
                        styles.targetIndicator,
                        {
                          left: `${gap.targetLevel * 100}%`,
                          backgroundColor: priorityColor,
                        },
                      ]}
                    />
                  </View>
                  <Text variant='bodySmall' style={styles.gapSize}>
                    Gap: {Math.round(gap.gap * 100)}%
                  </Text>
                </View>
              </View>

              {/* Meta Information */}
              <View style={styles.gapMeta}>
                <Chip
                  compact
                  style={[
                    styles.priorityChip,
                    { backgroundColor: priorityColor + '20' },
                  ]}
                  textStyle={{ color: priorityColor }}
                >
                  {gap.priority.toUpperCase()}
                </Chip>
                <Text variant='bodySmall' style={styles.timeEstimate}>
                  {formatTime(gap.estimatedTimeToClose)}
                </Text>
                {gap.blockedSkills.length > 0 && (
                  <Text variant='bodySmall' style={styles.blockedSkills}>
                    Blocks {gap.blockedSkills.length} skills
                  </Text>
                )}
              </View>
            </View>

            <IconButton
              icon={isExpanded ? 'chevron-up' : 'chevron-down'}
              size={20}
            />
          </View>
        </TouchableOpacity>

        {isExpanded && (
          <View style={styles.gapDetails}>
            {/* Prerequisites */}
            {gap.prerequisites.length > 0 && (
              <View style={styles.prerequisitesSection}>
                <Text variant='labelLarge' style={styles.sectionTitle}>
                  Prerequisites
                </Text>
                <View style={styles.prerequisitesList}>
                  {gap.prerequisites.map(prereq => (
                    <Chip key={prereq} compact style={styles.prerequisiteChip}>
                      {prereq}
                    </Chip>
                  ))}
                </View>
              </View>
            )}

            {/* AI Recommendations */}
            <View style={styles.recommendationsSection}>
              <Text variant='labelLarge' style={styles.sectionTitle}>
                AI Recommendations
              </Text>
              {gap.aiRecommendations.slice(0, 3).map(rec => (
                <TouchableOpacity
                  key={rec.id}
                  style={styles.recommendationItem}
                  onPress={() => onRecommendationSelect(rec)}
                >
                  <View style={styles.recommendationContent}>
                    <View style={styles.recommendationHeader}>
                      <Text
                        variant='titleSmall'
                        style={styles.recommendationTitle}
                      >
                        {rec.title}
                      </Text>
                      <ConfidenceIndicator
                        confidence={rec.confidence}
                        size='small'
                        showLabel={false}
                      />
                    </View>
                    <Text
                      variant='bodySmall'
                      style={styles.recommendationDescription}
                    >
                      {rec.description}
                    </Text>
                    <View style={styles.recommendationMeta}>
                      <Text variant='bodySmall' style={styles.impactText}>
                        Impact: +{Math.round(rec.estimatedImpact * 100)}%
                      </Text>
                      <Text variant='bodySmall' style={styles.timeText}>
                        {formatTime(rec.timeRequired)}
                      </Text>
                    </View>
                  </View>
                  <IconButton icon='chevron-right' size={16} />
                </TouchableOpacity>
              ))}
            </View>

            {/* Learning Path Preview */}
            {gap.learningPath.length > 0 && (
              <View style={styles.learningPathSection}>
                <Text variant='labelLarge' style={styles.sectionTitle}>
                  Suggested Learning Path
                </Text>
                <View style={styles.pathPreview}>
                  {gap.learningPath.slice(0, 3).map((step, index) => (
                    <View key={step.id} style={styles.pathStep}>
                      <View style={styles.stepNumber}>
                        <Text
                          variant='labelSmall'
                          style={styles.stepNumberText}
                        >
                          {index + 1}
                        </Text>
                      </View>
                      <View style={styles.stepContent}>
                        <Text variant='titleSmall' style={styles.stepTitle}>
                          {step.title}
                        </Text>
                        <Text variant='bodySmall' style={styles.stepTime}>
                          {formatTime(step.estimatedTime)}
                        </Text>
                      </View>
                    </View>
                  ))}
                  {gap.learningPath.length > 3 && (
                    <Text variant='bodySmall' style={styles.moreSteps}>
                      +{gap.learningPath.length - 3} more steps
                    </Text>
                  )}
                </View>
              </View>
            )}

            {/* Action Buttons */}
            <View style={styles.actionButtons}>
              <Button
                mode='outlined'
                onPress={() => onGapFocus(gap.skillId)}
                style={styles.actionButton}
              >
                Focus on This
              </Button>
              <Button
                mode='contained'
                onPress={() =>
                  onLearningPathStart(gap.skillId, gap.learningPath)
                }
                style={styles.actionButton}
              >
                Start Learning
              </Button>
            </View>
          </View>
        )}
      </Card>
    );
  };

  const renderDependencyView = () => (
    <ScrollView style={styles.dependencyContainer}>
      <Text variant='titleMedium' style={styles.dependencyTitle}>
        Skill Dependencies
      </Text>
      <Text variant='bodyMedium' style={styles.dependencyDescription}>
        Understanding skill relationships helps prioritize learning paths
      </Text>

      {/* This would render a dependency graph visualization */}
      <Card style={styles.dependencyCard}>
        <View style={styles.dependencyPlaceholder}>
          <IconButton icon='graph' size={48} />
          <Text variant='titleMedium'>Dependency Graph</Text>
          <Text variant='bodyMedium' style={styles.placeholderText}>
            Interactive skill dependency visualization coming soon
          </Text>
        </View>
      </Card>
    </ScrollView>
  );

  const renderContent = () => {
    if (viewMode === 'dependencies') {
      return renderDependencyView();
    }

    return (
      <ScrollView style={styles.content}>
        {filteredGaps.map(renderGapCard)}
      </ScrollView>
    );
  };

  return (
    <Surface style={[styles.container, style]} elevation={1}>
      {/* Header */}
      <View style={styles.header}>
        <Text variant='headlineSmall' style={styles.title}>
          Skill Gap Analysis
        </Text>

        {/* Statistics */}
        <View style={styles.statsContainer}>
          <View style={styles.statItem}>
            <Text variant='headlineSmall' style={styles.statValue}>
              {gapStats.totalGaps}
            </Text>
            <Text variant='bodySmall' style={styles.statLabel}>
              Total Gaps
            </Text>
          </View>
          <View style={styles.statItem}>
            <Text
              variant='headlineSmall'
              style={[styles.statValue, { color: '#F57C00' }]}
            >
              {gapStats.criticalGaps + gapStats.highGaps}
            </Text>
            <Text variant='bodySmall' style={styles.statLabel}>
              High Priority
            </Text>
          </View>
          <View style={styles.statItem}>
            <Text variant='headlineSmall' style={styles.statValue}>
              {Math.round(gapStats.totalTimeToClose / 60)}h
            </Text>
            <Text variant='bodySmall' style={styles.statLabel}>
              Est. Time
            </Text>
          </View>
        </View>

        {/* View Mode Selector */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.viewModeContainer}
        >
          {(
            ['priority', 'category', 'timeline', 'dependencies'] as ViewMode[]
          ).map(mode => (
            <Chip
              key={mode}
              selected={viewMode === mode}
              onPress={() => setViewMode(mode)}
              style={styles.viewModeChip}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </Chip>
          ))}
        </ScrollView>

        {/* Filters */}
        {viewMode !== 'dependencies' && (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.filtersContainer}
          >
            {(['all', 'critical', 'high', 'medium', 'low'] as FilterMode[]).map(
              filter => (
                <Chip
                  key={filter}
                  selected={filterMode === filter}
                  onPress={() => setFilterMode(filter)}
                  style={styles.filterChip}
                  compact
                >
                  {filter.toUpperCase()}
                </Chip>
              )
            )}
          </ScrollView>
        )}
      </View>

      {/* Content */}
      {renderContent()}
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    margin: 16,
    borderRadius: 12,
  },
  header: {
    padding: 16,
    paddingBottom: 8,
  },
  title: {
    marginBottom: 16,
    fontWeight: '600',
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 16,
    paddingVertical: 12,
    backgroundColor: 'rgba(103, 80, 164, 0.05)',
    borderRadius: 8,
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontWeight: 'bold',
  },
  statLabel: {
    opacity: 0.7,
    marginTop: 4,
  },
  viewModeContainer: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  viewModeChip: {
    marginRight: 8,
  },
  filtersContainer: {
    flexDirection: 'row',
  },
  filterChip: {
    marginRight: 8,
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
  },

  // Gap Card Styles
  gapCard: {
    marginBottom: 12,
  },
  gapHeader: {
    padding: 16,
  },
  gapHeaderContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  gapInfo: {
    flex: 1,
  },
  gapTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  gapTitleContent: {
    flex: 1,
    marginLeft: 8,
  },
  gapTitle: {
    fontWeight: '600',
  },
  gapCategory: {
    opacity: 0.7,
    marginTop: 2,
  },
  progressSection: {
    marginBottom: 12,
  },
  progressLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  progressLabel: {
    opacity: 0.7,
  },
  progressContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  progressTrack: {
    flex: 1,
    height: 8,
    backgroundColor: 'rgba(0,0,0,0.1)',
    borderRadius: 4,
    position: 'relative',
  },
  currentProgress: {
    height: '100%',
    borderRadius: 4,
  },
  targetIndicator: {
    position: 'absolute',
    top: -2,
    width: 3,
    height: 12,
    borderRadius: 1.5,
  },
  gapSize: {
    opacity: 0.7,
    minWidth: 60,
    textAlign: 'right',
  },
  gapMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  priorityChip: {
    height: 24,
  },
  timeEstimate: {
    opacity: 0.7,
  },
  blockedSkills: {
    opacity: 0.7,
    color: '#F57C00',
  },

  // Gap Details Styles
  gapDetails: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  prerequisitesSection: {
    marginBottom: 16,
  },
  sectionTitle: {
    marginBottom: 8,
    fontWeight: '600',
  },
  prerequisitesList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  prerequisiteChip: {
    backgroundColor: 'rgba(103, 80, 164, 0.1)',
  },
  recommendationsSection: {
    marginBottom: 16,
  },
  recommendationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: 'rgba(103, 80, 164, 0.05)',
    borderRadius: 8,
    marginBottom: 8,
  },
  recommendationContent: {
    flex: 1,
  },
  recommendationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  recommendationTitle: {
    fontWeight: '600',
    flex: 1,
  },
  recommendationDescription: {
    marginBottom: 8,
    lineHeight: 18,
  },
  recommendationMeta: {
    flexDirection: 'row',
    gap: 16,
  },
  impactText: {
    color: '#4CAF50',
    fontWeight: '600',
  },
  timeText: {
    opacity: 0.7,
  },
  learningPathSection: {
    marginBottom: 16,
  },
  pathPreview: {
    gap: 8,
  },
  pathStep: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  stepNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#6750A4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepNumberText: {
    color: 'white',
    fontWeight: 'bold',
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontWeight: '600',
  },
  stepTime: {
    opacity: 0.7,
    marginTop: 2,
  },
  moreSteps: {
    textAlign: 'center',
    opacity: 0.7,
    marginTop: 8,
  },
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    flex: 1,
  },

  // Dependency View Styles
  dependencyContainer: {
    flex: 1,
    padding: 16,
  },
  dependencyTitle: {
    fontWeight: '600',
    marginBottom: 8,
  },
  dependencyDescription: {
    opacity: 0.7,
    marginBottom: 16,
  },
  dependencyCard: {
    padding: 32,
  },
  dependencyPlaceholder: {
    alignItems: 'center',
    gap: 12,
  },
  placeholderText: {
    textAlign: 'center',
    opacity: 0.7,
  },
});
